/**
 * The screen that is not blank.
 *
 * React unmounts the entire tree when a render throws, and what a player sees
 * is white. Nothing about that says the game is still running, which it is -
 * the room, the seat and the cards are all on the server, and a reload
 * rejoins.
 *
 * Added after exactly that: a hook declared below an early return meant one
 * component ran a different number of hooks for a host than for a non-host,
 * and the moment somebody inherited the room mid-game their screen went white
 * with a running game behind it.
 *
 * So this is not for that bug, which is fixed. It is for the next one. A
 * render error in any single component should cost that player a reload, not
 * their seat.
 *
 * ## Why a class
 *
 * `componentDidCatch` and `getDerivedStateFromError` have no hook equivalent.
 * This is the one component in the app that has to be a class, and it is worth
 * saying so rather than leaving somebody to wonder.
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

interface State {
  failed: boolean;
  message: string;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { failed: false, message: '' };

  static getDerivedStateFromError(error: unknown): State {
    return {
      failed: true,
      message: error instanceof Error ? error.message : String(error),
    };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // The console is where this is actually diagnosed. The screen below says
    // what to do; this says what happened, with the component stack that
    // names the culprit.
    console.error('render failed', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;

    return (
      <div className="page narrow" role="alert">
        <h1 className="page-title">Something broke on this screen</h1>
        {/*
          The reassurance first, because it is the part that is not obvious:
          a white screen looks like the game is gone, and it is not.
        */}
        <p className="field-help">
          Your seat and the game are still there — this is only the screen. Reloading
          rejoins the table.
        </p>
        <button type="button" className="big-button primary" onClick={() => window.location.reload()}>
          Reload
        </button>
        {/*
          The message, quietly. It is developer text - the same reasoning as
          describeMatchError - but a player reporting "it broke" is far more
          useful to us with one line to quote.
        */}
        <p className="field-help">{this.state.message}</p>
      </div>
    );
  }
}
